/**
 * HMVMReviewer Theme Management Utilities
 * 
 * Centralized theme management for base color scheme (dark/light/system) and product accent themes.
 * Handles localStorage persistence, DOM attribute synchronization, and cross-tab communication.
 */

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Base theme mode (user preference)
 */
export type ReviewerThemeBase = "dark" | "light" | "system";

/**
 * Resolved base theme (actual dark/light value)
 */
export type ReviewerThemeBaseResolved = "dark" | "light";

/**
 * Product accent theme variants
 */
export type ReviewerThemeAccent = "reviewer-electric-blue" | "reviewer-neutral";

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY_BASE = "reviewer:theme-base";
const STORAGE_KEY_ACCENT = "reviewer:theme-accent";
const DEFAULT_THEME_BASE: ReviewerThemeBase = "dark";
const DEFAULT_THEME_ACCENT: ReviewerThemeAccent = "reviewer-electric-blue";

// ============================================================================
// Theme Accent Labels (for UI)
// ============================================================================

export const REVIEWER_THEME_ACCENT_LABELS: Record<ReviewerThemeAccent, string> = {
  "reviewer-electric-blue": "Electric Blue",
  "reviewer-neutral": "Neutral",
};

// ============================================================================
// Storage Functions
// ============================================================================

/**
 * Read stored base theme from localStorage
 */
export function readStoredThemeBase(): ReviewerThemeBase {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_BASE);
    if (stored === "dark" || stored === "light" || stored === "system") {
      return stored;
    }
  } catch (e) {
    // localStorage access error (private browsing, etc.)
  }
  return DEFAULT_THEME_BASE;
}

/**
 * Read stored accent theme from localStorage
 */
export function readStoredThemeAccent(): ReviewerThemeAccent {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_ACCENT);
    if (stored === "reviewer-electric-blue" || stored === "reviewer-neutral") {
      return stored;
    }
  } catch (e) {
    // localStorage access error
  }
  return DEFAULT_THEME_ACCENT;
}

/**
 * Write base theme to localStorage
 */
function writeStoredThemeBase(base: ReviewerThemeBase): void {
  try {
    localStorage.setItem(STORAGE_KEY_BASE, base);
  } catch (e) {
    // Ignore storage errors
  }
}

/**
 * Write accent theme to localStorage
 */
function writeStoredThemeAccent(accent: ReviewerThemeAccent): void {
  try {
    localStorage.setItem(STORAGE_KEY_ACCENT, accent);
  } catch (e) {
    // Ignore storage errors
  }
}

// ============================================================================
// Theme Resolution
// ============================================================================

/**
 * Resolve "system" base theme to actual dark/light based on OS preference
 */
export function resolveThemeBase(base: ReviewerThemeBase): ReviewerThemeBaseResolved {
  if (base === "system") {
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
    }
    return "dark"; // Fallback
  }
  return base;
}

// ============================================================================
// DOM Attribute Synchronization
// ============================================================================

/**
 * Apply base theme to DOM
 */
function applyThemeBaseToDOM(base: ReviewerThemeBase, resolved: ReviewerThemeBaseResolved): void {
  if (typeof document === "undefined") return;
  
  document.documentElement.setAttribute("data-reviewer-theme-base-mode", base);
  document.documentElement.setAttribute("data-reviewer-theme-base", resolved);
  document.documentElement.style.colorScheme = resolved;
}

/**
 * Apply accent theme to DOM
 */
function applyThemeAccentToDOM(accent: ReviewerThemeAccent): void {
  if (typeof document === "undefined") return;
  
  document.documentElement.setAttribute("data-reviewer-theme", accent);
}

// ============================================================================
// Public Setters
// ============================================================================

/**
 * Set base theme (dark/light/system) and persist to localStorage
 * Returns the resolved theme value
 */
export function setThemeBase(base: ReviewerThemeBase): ReviewerThemeBaseResolved {
  const resolved = resolveThemeBase(base);
  writeStoredThemeBase(base);
  applyThemeBaseToDOM(base, resolved);
  return resolved;
}

/**
 * Set accent theme and persist to localStorage
 */
export function setThemeAccent(accent: ReviewerThemeAccent): void {
  writeStoredThemeAccent(accent);
  applyThemeAccentToDOM(accent);
}

// ============================================================================
// System Preference Watcher
// ============================================================================

/**
 * Watch for OS theme preference changes (for "system" mode)
 * Returns cleanup function
 */
export function watchThemeSystem(): () => void {
  if (typeof window === "undefined" || !window.matchMedia) {
    return () => {}; // No-op in SSR
  }
  
  const mediaQuery = window.matchMedia("(prefers-color-scheme: light)");
  
  const listener = () => {
    const currentBase = readStoredThemeBase();
    if (currentBase === "system") {
      const resolved = resolveThemeBase(currentBase);
      applyThemeBaseToDOM(currentBase, resolved);
    }
  };
  
  // Modern API (Safari 14+)
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener("change", listener);
    return () => mediaQuery.removeEventListener("change", listener);
  }
  
  // Legacy API
  mediaQuery.addListener(listener);
  return () => mediaQuery.removeListener(listener);
}

// ============================================================================
// Initialization (FOUC Prevention Support)
// ============================================================================

/**
 * Initialize theme on app startup (called in main.tsx before React render)
 * This ensures theme is applied even if inline script in index.html didn't run
 */
export function initReviewerTheme(): void {
  const base = readStoredThemeBase();
  const accent = readStoredThemeAccent();
  const resolved = resolveThemeBase(base);
  
  applyThemeBaseToDOM(base, resolved);
  applyThemeAccentToDOM(accent);
}
